import { useState, useRef, useEffect, useMemo } from 'react';

interface ComboboxProps {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  className?: string;
}

/**
 * Free-text input with an autocomplete dropdown of existing values.
 * Typing a value that isn't in the list is allowed — it surfaces an explicit
 * "Add «…»" entry so the user sees they're creating a new variant.
 */
export function Combobox({
  value,
  options,
  onChange,
  placeholder,
  id,
  className = '',
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const trimmed = value.trim();
  const lowered = trimmed.toLowerCase();

  const filtered = useMemo(() => {
    const seen = new Set<string>();
    const dedup: string[] = [];
    for (const o of options) {
      const k = o.toLowerCase();
      if (!seen.has(k)) {
        seen.add(k);
        dedup.push(o);
      }
    }
    if (!trimmed) return dedup;
    return dedup.filter((o) => o.toLowerCase().includes(lowered));
  }, [options, trimmed, lowered]);

  const exactMatch = useMemo(
    () => filtered.some((o) => o.toLowerCase() === lowered),
    [filtered, lowered],
  );

  const showAddNew = trimmed.length > 0 && !exactMatch;
  const totalRows = filtered.length + (showAddNew ? 1 : 0);

  useEffect(() => {
    if (highlight >= totalRows) setHighlight(0);
  }, [highlight, totalRows]);

  function commit(v: string) {
    onChange(v);
    setOpen(false);
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlight((h) => (totalRows === 0 ? 0 : (h + 1) % totalRows));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (totalRows === 0 ? 0 : (h - 1 + totalRows) % totalRows));
    } else if (e.key === 'Enter') {
      if (!open) return;
      e.preventDefault();
      if (showAddNew && highlight === 0) {
        commit(trimmed);
      } else {
        const idx = showAddNew ? highlight - 1 : highlight;
        const pick = filtered[idx];
        if (pick) commit(pick);
        else if (trimmed) commit(trimmed);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKey}
        placeholder={placeholder}
        className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
        autoComplete="off"
      />
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
          inputRef.current?.focus();
        }}
        tabIndex={-1}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-muted"
        aria-label="Toggle suggestions"
      >
        <svg
          className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (filtered.length > 0 || showAddNew) && (
        <div className="absolute z-50 mt-1 w-full bg-surface border border-border rounded-md shadow-lg overflow-hidden">
          <div className="max-h-60 overflow-y-auto py-1">
            {showAddNew && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(trimmed)}
                onMouseEnter={() => setHighlight(0)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                  highlight === 0 ? 'bg-primary/10 text-primary' : 'text-content-muted hover:bg-surface-muted'
                }`}
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="truncate">
                  Add <span className="font-medium">«{trimmed}»</span>
                </span>
              </button>
            )}
            {filtered.map((opt, i) => {
              const rowIdx = showAddNew ? i + 1 : i;
              const isActive = opt.toLowerCase() === lowered;
              return (
                <button
                  key={opt}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => commit(opt)}
                  onMouseEnter={() => setHighlight(rowIdx)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                    highlight === rowIdx
                      ? 'bg-primary/10 text-primary'
                      : isActive
                        ? 'bg-surface-muted text-content font-medium'
                        : 'text-content-muted hover:bg-surface-muted'
                  }`}
                >
                  <span className="truncate">{opt}</span>
                  {isActive && (
                    <svg className="w-4 h-4 ml-auto text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
